import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Mail, 
  CheckCircle, 
  XCircle, 
  Send, 
  Users, 
  Search,
  Loader2 
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterManager() {
  const [subscribers, setSubscribers] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, verified, pending
  const [stats, setStats] = useState({ total: 0, verified: 0, pending: 0 });
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.log('NewsletterManager: No token in localStorage');
      setLoading(false);
      return;
    }
    
    try {
      console.log('NewsletterManager: Fetching data with token');
      setLoading(true);
      
      // Fetch subscribers
      const verifiedParam = filter === 'all' ? '' : `?verified=${filter === 'verified'}`;
      const subsResponse = await axios.get(
        `${BACKEND_URL}/api/admin/blog/newsletter/subscribers${verifiedParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSubscribers(subsResponse.data.subscribers || []);
      
      // Fetch stats
      const allSubs = await axios.get(
        `${BACKEND_URL}/api/admin/blog/newsletter/subscribers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const total = allSubs.data.total || 0;
      const verified = allSubs.data.subscribers?.filter(s => s.verified).length || 0;
      setStats({ total, verified, pending: total - verified });
      
      // Fetch recent published blog posts
      const postsResponse = await axios.get(
        `${BACKEND_URL}/api/admin/blog/posts?status=published&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBlogPosts(postsResponse.data.posts || []);
      
    } catch (error) {
      console.error('Error fetching newsletter data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load newsletter data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const sendNewsletter = async (postId) => {
    if (!window.confirm('Are you sure you want to send this newsletter to all verified subscribers?')) {
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/blog/newsletter/send?post_id=${postId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Newsletter Sent!',
        description: `Sent to ${response.data.sent} subscribers`,
      });
      
    } catch (error) {
      console.error('Error sending newsletter:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send newsletter',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const filteredSubscribers = subscribers.filter(sub =>
    sub.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        <p className="ml-3 text-gray-600 dark:text-gray-400">Loading newsletter data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Subscribers</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
              <Users className="w-10 h-10 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Verified</p>
                <p className="text-3xl font-bold text-green-600">{stats.verified}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
                <p className="text-3xl font-bold text-orange-600">{stats.pending}</p>
              </div>
              <Mail className="w-10 h-10 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Send Newsletter Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Send Newsletter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Select a published blog post to send as newsletter to all verified subscribers.
          </p>
          
          {blogPosts.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">No published posts available</p>
          ) : (
            <div className="space-y-3">
              {blogPosts.slice(0, 5).map(post => (
                <div key={post.id} className="flex items-center justify-between p-3 border dark:border-gray-700 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">{post.title}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(post.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    onClick={() => sendNewsletter(post.id)}
                    disabled={sending || stats.verified === 0}
                    size="sm"
                    className="ml-4"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send to {stats.verified}
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscribers List */}
      <Card>
        <CardHeader>
          <CardTitle>Subscribers</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                size="sm"
              >
                All ({stats.total})
              </Button>
              <Button
                variant={filter === 'verified' ? 'default' : 'outline'}
                onClick={() => setFilter('verified')}
                size="sm"
              >
                Verified ({stats.verified})
              </Button>
              <Button
                variant={filter === 'pending' ? 'default' : 'outline'}
                onClick={() => setFilter('pending')}
                size="sm"
              >
                Pending ({stats.pending})
              </Button>
            </div>
          </div>

          {/* Subscribers Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Subscribed</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredSubscribers.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      No subscribers found
                    </td>
                  </tr>
                ) : (
                  filteredSubscribers.map((sub, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:bg-gray-700">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{sub.email}</td>
                      <td className="px-4 py-3">
                        {sub.verified ? (
                          <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                            <Mail className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(sub.subscribed_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
